import { CallButton } from '@/components/CallButton';
import { TextButton } from '@/components/TextButton';
import { ActionIcon, Group, Tooltip } from '@mantine/core';
import { Organization, Patient, Reference } from '@medplum/fhirtypes';
import { IconPlayerPlay } from '@tabler/icons-react';
import { ReactElement } from 'react';

interface CommunicationLogActionsProps {
  /** The external number on the row. Without one there is nobody to call or text. */
  phoneNumber?: string;
  /** The patient or partner the row is with, so a call back is filed where the text would be. */
  recipient?: Reference<Patient | Organization>;
  onText: () => void;
  /** Only on a call that has a recording. */
  onPlayRecording?: () => void;
}

/**
 * The action buttons on a Phone logs or SMS logs row: play the recording, call back, text.
 *
 * One component so the two logs cannot drift — the same buttons in the same order, and the
 * same `light` treatment on all three so they read as one row of actions rather than one
 * highlighted button beside two quiet ones. Every button stops propagation, because the row
 * behind it navigates.
 *
 * Calling back and texting stay whether or not there is a recording: only about four rows in
 * five have one, and an unknown caller — the row somebody most wants to reach — is exactly
 * the row with nothing else on it.
 */
export function CommunicationLogActions({
  phoneNumber,
  recipient,
  onText,
  onPlayRecording,
}: CommunicationLogActionsProps): ReactElement {
  return (
    <Group gap={4} wrap="nowrap">
      {onPlayRecording && (
        <Tooltip label="Play recording">
          <ActionIcon
            variant="light"
            color="blue"
            aria-label="Play recording"
            onClick={(event) => {
              event.stopPropagation();
              onPlayRecording();
            }}
          >
            <IconPlayerPlay size={14} />
          </ActionIcon>
        </Tooltip>
      )}
      {phoneNumber && (
        <>
          <CallButton phoneNumber={phoneNumber} recipient={recipient} tooltip="Call" />
          <TextButton onClick={onText} />
        </>
      )}
    </Group>
  );
}
